import { useMemo, useState } from 'react'
import compact from 'lodash/compact'
import toString from 'lodash/toString'
import toInteger from 'lodash/toInteger'
import { dateFormatted, listItemAmountFormatted, listItemEffectiveDate } from '@klear/order-event/list-display'
import { usePurchaseOrders } from '@klear/ui/hooks/use-purchase-orders'
import type { ComboboxOption } from '@klear/ui/components/combobox'
import { EntitySelectOrCreate } from '@/app/workflow/EntitySelectOrCreate'

export type PurchaseOrderSelectOrCreateProps = {
  orderId: number
  defaultSelectedId?: number | null
  onAttach: (purchaseOrderEventId: number) => void
  onCreateNew: () => void
}

export function PurchaseOrderSelectOrCreate({
  orderId,
  defaultSelectedId,
  onAttach,
  onCreateNew
}: PurchaseOrderSelectOrCreateProps) {
  const { purchaseOrders: existingPurchaseOrders, isLoading } = usePurchaseOrders(
    orderId ? { order: [orderId] } : undefined,
    { requiredFilters: ['order'] }
  )

  const [selectedExistingId, setSelectedExistingId] = useState<number | null>(defaultSelectedId ?? null)

  const purchaseOrderOptions = useMemo<ComboboxOption[]>(
    () =>
      existingPurchaseOrders.map((event) => {
        const idLabel = `#${event.idDisplay}`
        const numberText = event.number || null
        const amountText = event.amount ? listItemAmountFormatted(event) : null
        const effectiveDate = listItemEffectiveDate(event)
        const dateText = effectiveDate ? dateFormatted(effectiveDate) : null
        const left = compact([idLabel, numberText]).join(' · ')
        const label = (
          <span className="flex w-full items-center justify-between gap-4">
            <span>{left}</span>
            {compact([amountText, dateText]).length > 0 && (
              <span className="tabular-nums text-muted-foreground ml-auto">
                {compact([amountText, dateText]).join(' · ')}
              </span>
            )}
          </span>
        )
        return {
          value: toString(event.id),
          label,
          keywords: compact([idLabel, numberText, amountText, dateText])
        }
      }),
    [existingPurchaseOrders]
  )

  return (
    <EntitySelectOrCreate
      inlineCreate
      isLoading={isLoading}
      selectTitle="Attach a purchase order"
      selectDescription="Attach this payable's operational event to a purchase order on this order, or create a new one."
      selectFieldLabel="Purchase order"
      options={purchaseOrderOptions}
      selectedValue={selectedExistingId != null ? toString(selectedExistingId) : ''}
      onSelectedChange={(v) => setSelectedExistingId(v ? toInteger(v) : null)}
      onSelectConfirm={() => selectedExistingId != null && onAttach(selectedExistingId)}
      selectConfirmDisabled={selectedExistingId == null}
      selectTriggerLabel="Select purchase order…"
      selectPlaceholder="Search purchase orders…"
      createTitle="Create a new purchase order"
      createDescription="Build the purchase order for this order. The operational event in the next step hangs off of it."
      createActionLabel="Create"
      onCreateNew={onCreateNew}
    />
  )
}
